import { useState, useEffect, useRef } from 'react';
import { Key, AlertCircle, Loader2, QrCode } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface TokenInputProps {
  onSubmit: (token: string) => Promise<boolean>;
  initialToken?: string;
}

export function TokenInput({ onSubmit, initialToken }: TokenInputProps) {
  const [token, setToken] = useState(initialToken || '');
  const [fromQR, setFromQR] = useState(!!initialToken);
  const hasSetInitial = useRef(false);

  // Set initial token only once when provided via QR code
  useEffect(() => {
    if (initialToken && !hasSetInitial.current) {
      setToken(initialToken);
      setFromQR(true);
      hasSetInitial.current = true;
    }
  }, [initialToken]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedToken = token.trim();
    if (!trimmedToken) {
      setError('Please enter a token');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const success = await onSubmit(trimmedToken);
      if (!success) {
        setError('Invalid token. Please check and try again.');
      }
    } catch (err) {
      setError('Failed to verify token. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {fromQR && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          <QrCode className="h-4 w-4 flex-shrink-0" />
          Token scanned from QR code. Tap Connect to continue.
        </div>
      )}
      <div>
        <label
          htmlFor="token"
          className="mb-2 block text-sm font-medium text-muted-foreground"
        >
          Device Token
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
            <Key className="h-5 w-5 text-muted-foreground" />
          </div>
          <Input
            type="password"
            id="token"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              setError(null);
            }}
            placeholder="Paste your device token"
            className="pl-10 text-sm"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/12 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Button type="submit" disabled={isLoading} className="w-full">
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Verifying...
          </>
        ) : (
          'Connect'
        )}
      </Button>
    </form>
  );
}
